<script setup lang="ts">
import { computed } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import mergePropsWithDefaults from 'lib/utils/mergePropsWithDefaults.util';

import type {
  FieldWrapperEmits,
  FieldWrapperProps,
  FieldWrapperSlots,
} from './FieldWrapper.vue.d';
import Icon from '../icon/Icon.vue';

const props = withDefaults(defineProps<FieldWrapperProps>(), {
  showOptionalText: undefined,
  showAsterisk: undefined,
});

defineEmits<FieldWrapperEmits>();

const slots = defineSlots<FieldWrapperSlots>();

const { defaultProps, locale } = useWangsVueConfig('FieldWrapper');
const config = computed(() => mergePropsWithDefaults(props, defaultProps));
</script>

<template>
  <slot v-if="$attrs['use-field-wrapper'] == false" />

  <div v-else v-bind="$preset.fieldwrapper.root">
    <label
      v-if="label || slots['label-addon']"
      v-bind="$preset.fieldwrapper.label({ props, slots })"
    >
      <template v-if="label">
        {{ label }}

        <!-- Show the required asterisk if the field is mandatory -->
        <span
          v-if="mandatory && config.showAsterisk"
          v-bind="$preset.fieldwrapper.arterisksign"
        >
          {{ locale.labelRequired }}
        </span>

        <!-- Info icon if info prop is provided -->
        <Icon
          v-if="info"
          :info="info"
          :tooltip-pos="config.tooltipPos"
          @click="$emit('infoClick')"
          v-bind="$preset.fieldwrapper.info"
          icon="info"
        />

        <!-- Show (optional) text if the field is not mandatory -->
        <span
          v-if="mandatory == false && config.showOptionalText"
          v-bind="$preset.fieldwrapper.optionaltext"
        >
          {{ locale.labelOptional }}
        </span>
      </template>

      <slot name="label-addon" />
    </label>

    <slot />
  </div>
</template>
